import { useEffect, useState, useCallback } from 'react';
import { Plus, X, Save, AlertTriangle, Edit, MapPin } from 'lucide-react';
import { api } from '../../../api/client';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';

type Location = {
  id: string;
  name: string;
  address: string;
  city: string;
  posMode: 'store' | 'restaurant';
  storeLayout: 'modern' | 'retro';
  stockMode: 'strict' | 'warn' | 'lenient';
  restrictPayments: boolean;
  hasCertificate?: boolean;
  salesCount?: number;
};

const POS_MODE_LABELS: Record<string, { label: string; color: string }> = {
  store: { label: 'Prodavnica', color: '#3b82f6' },
  restaurant: { label: 'Restoran (kelner)', color: '#f59e0b' },
};

const STOCK_MODE_LABELS: Record<string, string> = {
  strict: 'Strict (blokira minus)',
  warn: 'Warn (dozvoli + log)',
  lenient: 'Lenient (sve dozvoli)',
};

export function LocationsTab({ tenantId }: { tenantId: string }) {
  const [locations, setLocations] = useState<Location[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Location | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const rows = await api.get<Location[]>(`/admin/locations?tenantId=${tenantId}`);
      setLocations(rows);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    }
  }, [tenantId]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--color-text-strong)', fontWeight: 600 }}>Lokacije ({locations.length})</h3>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(true)} style={btnPrimary}><Plus size={14} /> Nova lokacija</button>
      </div>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

      <div style={card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%' }}>
            <thead><tr style={{ background: 'var(--color-bg)' }}>
              <th style={th}>Naziv</th>
              <th style={th}>Grad</th>
              <th style={th}>POS režim</th>
              <th style={th}>Stock mod</th>
              <th style={{ ...th, textAlign: 'center' }}>Cert</th>
              <th style={{ ...th, textAlign: 'right', width: 60 }}>Akcije</th>
            </tr></thead>
            <tbody>
              {locations.length === 0 && (
                <tr><td colSpan={6} style={{ ...td, textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>
                  <MapPin size={28} style={{ margin: '0 auto 8px', opacity: 0.3 }} /> Nema lokacija
                </td></tr>
              )}
              {locations.map((l) => {
                const pm = POS_MODE_LABELS[l.posMode] || POS_MODE_LABELS.store;
                return (
                  <tr key={l.id}>
                    <td style={{ ...td, fontWeight: 600, color: 'var(--color-text-strong)' }}>{l.name}</td>
                    <td style={{ ...td, color: 'var(--color-text-muted)' }}>{l.city || '-'}</td>
                    <td style={td}>
                      <span style={{ ...badge, background: `${pm.color}22`, color: pm.color }}>{pm.label}</span>
                    </td>
                    <td style={{ ...td, fontSize: 12, color: 'var(--color-text-muted)' }}>{STOCK_MODE_LABELS[l.stockMode] || l.stockMode}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{l.hasCertificate ? '✓' : '-'}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button onClick={() => setEditing(l)} style={iconBtn}><Edit size={13} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {(creating || editing) && (
        <LocationFormDialog
          tenantId={tenantId}
          location={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function LocationFormDialog({ tenantId, location, onClose, onSaved }: { tenantId: string; location: Location | null; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const isEdit = !!location;
  const [name, setName] = useState(location?.name || '');
  const [address, setAddress] = useState(location?.address || '');
  const [city, setCity] = useState(location?.city || '');
  const [posMode, setPosMode] = useState<'store' | 'restaurant'>(location?.posMode || 'store');
  // Kolona `store_layout` ostaje u bazi zbog zatecenih zapisa; niko je vise ne
  // cita, pa se uvek salje 'modern'.
  const storeLayout = 'modern';
  const [stockMode, setStockMode] = useState<'strict' | 'warn' | 'lenient'>(location?.stockMode || 'strict');
  const [restrictPayments, setRestrictPayments] = useState(location?.restrictPayments || false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Naziv je obavezan'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = { name: name.trim(), address, city, posMode, storeLayout, stockMode, restrictPayments };
      if (isEdit) {
        await api.put(`/admin/locations/${location.id}`, body);
      } else {
        await api.post('/admin/locations', { ...body, tenantId });
      }
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>{isEdit ? `Izmena: ${location?.name}` : 'Nova lokacija'}</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Naziv *">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoFocus style={input} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Adresa"><input type="text" value={address} onChange={(e) => setAddress(e.target.value)} style={input} /></Field>
            <Field label="Grad"><input type="text" value={city} onChange={(e) => setCity(e.target.value)} style={input} /></Field>
          </div>

          <div style={{ padding: 14, background: 'var(--color-bg)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
            <h4 style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>POS podešavanja</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Režim rada *">
                <select value={posMode} onChange={(e) => {
                  const v = e.target.value as 'store' | 'restaurant';
                  setPosMode(v);
                  if (v === 'restaurant') setRestrictPayments(true);
                }} style={input}>
                  <option value="store">Prodavnica - klasičan POS</option>
                  <option value="restaurant">Restoran - kelner layout, stolovi</option>
                </select>
              </Field>
              {/* Izbor „Modern / Retro" uklonjen 2026-08-26 - retro ekran je ukinut.
                  Prikaz artikala se sada bira PO UREDJAJU u POS-u
                  (Podesavanja → POS prikaz), jer zavisi od kase, ne od lokacije. */}
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--color-text-muted)' }}>
              {posMode === 'store'
                ? '→ Prodavnica koristi standardni POS sa artiklima u mrežnom prikazu.'
                : '→ Restoran koristi kelner layout sa stolovima, podeljenim plaćanjima i režimom narudžbi.'}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Stock mod (politika zaliha)">
              <select value={stockMode} onChange={(e) => setStockMode(e.target.value as any)} style={input}>
                <option value="strict">Strict - blokira prodaju u minus</option>
                <option value="warn">Warn - dozvoli + log</option>
                <option value="lenient">Lenient - uvek dozvoli</option>
              </select>
            </Field>
            <Field label="">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={restrictPayments} onChange={(e) => setRestrictPayments(e.target.checked)} />
                <span style={{ fontSize: 13, color: 'var(--color-text)' }}>SUF P7: blokira Card/Check/Mobile</span>
              </label>
            </Field>
          </div>

          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving || !name.trim()} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div>{label && <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>}{children}</div>;
}

const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' };
const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const badge: React.CSSProperties = { display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '9px 16px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 560, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)', maxHeight: '90vh', overflow: 'auto' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)', position: 'sticky', top: 0, background: 'var(--color-bg-surface)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
